import React, { useState } from "react";
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiCopy } from "react-icons/fi";

type PageEditorUnknownComponentCardProps = {
	componentType?: string;
	content?: unknown;
};

/**
 * Read-only notice shown when a component's type has no visual editor.
 * Since every renderable type ships a field definition (enforced by the
 * library exhaustiveness test), this card should only ever appear for a
 * payload authored against a NEWER admin : the content is preserved
 * untouched on save, never editable as raw JSON.
 */
const PageEditorUnknownComponentCard: React.FC<PageEditorUnknownComponentCardProps> = ({
	componentType,
	content
}) => {
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const json = JSON.stringify(content ?? {}, null, 2);

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(json);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard unavailable (permissions / non-secure context) : ignore.
		}
	};

	return (
		<div className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-3">
			<div className="flex items-start gap-2">
				<FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
				<div className="min-w-0">
					<p className="text-sm font-semibold text-amber-800">
						Type de composant non pris en charge par l'éditeur
					</p>
					<p className="text-xs text-amber-700">
						« {componentType ?? "inconnu"} » n'a pas de formulaire d'édition dans cette version de
						l'administration. Son contenu est conservé tel quel à l'enregistrement.
					</p>
				</div>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					onClick={() => setOpen((o) => !o)}
					className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
					aria-expanded={open}
				>
					{open ? <FiChevronDown className="h-3.5 w-3.5" /> : <FiChevronRight className="h-3.5 w-3.5" />}
					{open ? "Masquer le contenu" : "Afficher le contenu"}
				</button>
				<button
					type="button"
					onClick={copy}
					className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
				>
					<FiCopy className="h-3.5 w-3.5" />
					{copied ? "Copié !" : "Copier le JSON"}
				</button>
			</div>
			{open && (
				<pre className="max-h-64 overflow-auto rounded-lg border border-amber-200 bg-white p-3 text-xs text-slate-700">
					{json}
				</pre>
			)}
		</div>
	);
};

export default PageEditorUnknownComponentCard;
